import { nanoid } from 'nanoid';
import {
  AggregatedAssessmentPage,
  ChecksAlert,
  ChecksGroup,
} from 'types/check-results';
import { getHeight, getLeft, getTop, getWidth } from './borderBox';
import {
  Check,
  ClaimNumber,
  ColorSuggestionValueKeys,
  ReviewCharacteristic,
  ReviewResult,
  SuggestionValue,
} from 'types/check';
import RGBColor, { IRGBColor } from 'classes/rgb-color';
import HEXColor from 'classes/hex-color';
import {
  AssessmentPagePayload,
  ChecksGroupPayload,
} from 'types/assessment-payload';

export function sortGroups(groups: ChecksGroup[]): ChecksGroup[] {
  return groups
    .sort((a, b) => {
      if (a.top < b.top) return -1;
      if (a.top > b.top) return 1;
      return 0;
    })
    .map(group => ({
      ...group,
      alerts: sortGroupAlerts(group.alerts),
    }));
}

export function sortGroupAlerts(alerts: ChecksAlert[]): ChecksAlert[] {
  return alerts.sort(({ borderBox: borderBoxA }, { borderBox: borderBoxB }) => {
    const isCrossing = !(
      borderBoxA.left > borderBoxB.left + borderBoxB.width ||
      borderBoxB.left > borderBoxA.left + borderBoxA.width ||
      borderBoxA.top > borderBoxB.top + borderBoxB.height ||
      borderBoxB.top > borderBoxA.top + borderBoxA.height
    );

    if (!isCrossing) {
      const sumA = borderBoxA.left + borderBoxA.top;
      const sumB = borderBoxB.left + borderBoxB.top;
      return sumA - sumB;
    }

    if (borderBoxA.top === borderBoxB.top)
      return borderBoxA.left - borderBoxB.left;

    return borderBoxA.top - borderBoxB.top;
  });
}

export function prepareAlerts(
  checkGroups: ChecksGroupPayload[]
): ChecksAlert[] {
  return checkGroups.reduce<ChecksAlert[]>(
    (alerts, { borderBox, checks, ...other }) => {
      let result = ReviewResult.pass;

      const checksCopy: Check[] = checks.map(check => {
        let checkCopy = { ...check } as Check;

        // todo refactor this
        if (check.characteristic === ReviewCharacteristic.claimsOrder) {
          checkCopy.claimsOrderRequiredValues =
            checkCopy.requiredValues as ClaimNumber[];
          checkCopy.claimsOrderUsedValues =
            checkCopy.usedValues as ClaimNumber[];
        } else {
          checkCopy.requiredValues =
            checkCopy.requiredValues as SuggestionValue[];
          checkCopy.usedValues = checkCopy.usedValues as SuggestionValue[];
        }

        if (check.characteristic === ReviewCharacteristic.fontColor) {
          checkCopy.usedValues = checkCopy.usedValues?.map(valueToColorClass);
          checkCopy.requiredValues =
            checkCopy.requiredValues?.map(valueToColorClass);
        }

        if (check.result === ReviewResult.fail) {
          result = ReviewResult.fail;
        }

        if (
          result !== ReviewResult.fail &&
          check.result === ReviewResult.warning
        ) {
          result = ReviewResult.warning;
        }

        return checkCopy;
      });

      alerts.push({
        ...other,
        externalId: other.id,
        id: nanoid(),
        result,
        checks: checksCopy,
        borderBox: {
          left: getLeft(borderBox),
          top: getTop(borderBox),
          width: getWidth(borderBox),
          height: getHeight(borderBox),
        },
      });

      return alerts;
    },
    []
  );
}

export function sortClaimsChecks(checks: Check[]): Check[] {
  const claimsOrder = [
    ReviewCharacteristic.claimStatus,
    ReviewCharacteristic.claimText,
    ReviewCharacteristic.claimsDependentContent,
    ReviewCharacteristic.claimsOrder,
  ];

  return claimsOrder.reduce<Check[]>((result, characteristic) => {
    const check = checks.find(check => characteristic === check.characteristic);

    if (check) result.push(check);

    return result;
  }, []);
}

export function preparePage({
  styleReview,
  brandingReview,
  safetyReview,
  claimsReview,
  ...other
}: AssessmentPagePayload): AggregatedAssessmentPage {
  return {
    ...other,
    styleReview: {
      groups: prepareAlerts(styleReview.groups),
      messages: styleReview.messages,
      errors: styleReview.errors || [],
    },
    safetyReview: {
      groups: prepareAlerts(safetyReview.groups),
      messages: safetyReview.messages.reverse(),
      errors: (safetyReview.errors || []).reverse(),
    },
    claimsReview: {
      groups: prepareAlerts(claimsReview.groups).map(alert => ({
        ...alert,
        checks: sortClaimsChecks(alert.checks),
      })),
      messages: claimsReview.messages,
      errors: claimsReview.errors || [],
    },
    brandingReview: {
      groups: prepareAlerts(brandingReview.groups),
      messages: brandingReview.messages,
      errors: brandingReview.errors || [],
    },
  };
}

function valueToColorClass(suggestion: SuggestionValue): SuggestionValue {
  const rgbValue = suggestion[ColorSuggestionValueKeys.rgb];
  const hexValue = suggestion[ColorSuggestionValueKeys.rgb];

  return {
    ...suggestion,
    [ColorSuggestionValueKeys.rgb]:
      rgbValue ?? new RGBColor(rgbValue as unknown as IRGBColor),
    [ColorSuggestionValueKeys.hex]:
      hexValue ?? new HEXColor(hexValue as unknown as string),
  } as SuggestionValue;
}
